import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface ChoongMooProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

/** Stance key, floor plan image, and numbered moveset — used in curriculum “Pattern” tab and legacy layout */
export function ChoongMooKeyAndMoveset() {
  return (
    <div className="space-y-4 pt-1">
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">KEY</h4>
        <div className="flex flex-col items-start gap-4 md:flex-row">
          <ul className="space-y-1 text-gray-700">
            <li>CS - Cat stance</li>
            <li>BS - Back stance</li>
            <li>FS - Front stance</li>
            <li>TS - Tiger stance</li>
            <li>KH - Knife Hand</li>
            <li>LS - L-Stance</li>
            <li>SS - Sitting Stance</li>
            <li>BRS - Bending Ready Stance</li>
            <li>FSS - Fixed Stance</li>
          </ul>
          <img
            src="/images/instructions/choong_moo_floorplan.png"
            alt="ChoongMoo Floor Plan"
            className="h-[160px] w-auto object-contain"
            loading="lazy"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">MOVESET</h4>
        <ol className="list-none space-y-2">
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">Start:</span>
            <span className="text-gray-700">Ready Posture - Parallel Ready Stance</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">1.</span>
            <span className="text-gray-700">Move the left foot to B forming a right L-Stance toward B while executing a twin knife-hand block.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">2.</span>
            <span className="text-gray-700">
              Move the right foot to B forming a right Front Stance toward B while executing a high front strike to B with the right knife-hand and bring
              the left back hand in front of the forehead.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">3.</span>
            <span className="text-gray-700">
              Move the right foot to A turning clockwise to form a left L-Stance toward A while executing a middle guarding block to A with a knife-hand.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">4.</span>
            <span className="text-gray-700">
              Move the left foot to A forming a left Front Stance toward A while executing a high thrust to A with the left flat finger tip.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">5.</span>
            <span className="text-gray-700">
              Move the left foot to D forming a right L-Stance toward D while executing a middle guarding block to D with a knife-hand.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">6.</span>
            <span className="text-gray-700">Turn the face to C forming a left Bending Ready Stance A toward C.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">7.</span>
            <span className="text-gray-700">Execute a middle side piercing kick to C with the right foot.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">8.</span>
            <span className="text-gray-700">
              Lower the right foot to C forming a right L-Stance toward D while executing a middle guarding block to D with a knife-hand.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">9.</span>
            <span className="text-gray-700">
              Execute a flying side piercing kick to D with the right foot soon after moving it to D and then land to D forming a left L-Stance toward D
              while executing a middle guarding block to D with a knife-hand.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">10.</span>
            <span className="text-gray-700">
              Move the left foot to E turning counter clockwise to form a right L-Stance toward E at the same time executing a low block to E with the
              left forearm.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">11.</span>
            <span className="text-gray-700">
              Extend both hands upward as if to grab the opponent&apos;s head while forming a left Front Stance toward E, slipping the left foot.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">12.</span>
            <span className="text-gray-700">Execute an upward kick to E with the right knee pulling both hands downward.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">13.</span>
            <span className="text-gray-700">
              Lower the right foot to the left foot and then move the left foot to F forming a left Front Stance toward F while executing a high front
              strike to F with the right reverse knife-hand, bringing the left back hand under the right elbow joint.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">14.</span>
            <span className="text-gray-700">Execute a high turning kick to DF with the right foot and then lower it to the left foot.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">15.</span>
            <span className="text-gray-700">Execute a middle back piercing kick to F with the left foot. Perform 14 and 15 in a fast motion.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">16.</span>
            <span className="text-gray-700">
              Lower the left foot to F forming a left L-Stance toward E while executing a middle guarding block to E with the forearm.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">17.</span>
            <span className="text-gray-700">Execute a middle turning kick to DE with the left foot.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">18.</span>
            <span className="text-gray-700">
              Lower the left foot to the right foot and then move the right foot to C forming a right Fixed Stance toward C while executing a U-shape
              block toward C.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">19.</span>
            <span className="text-gray-700">
              Jump and spin around counter clockwise, landing on the same spot to form a left L-Stance toward C while executing a middle guarding block
              to C with a knife-hand.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">20.</span>
            <span className="text-gray-700">
              Move the left foot to C forming a left Front Stance toward C at the same time executing a low thrust to C with the right upset fingertip.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">21.</span>
            <span className="text-gray-700">
              Execute a side back strike to D with the right back fist and a low block to C with the left forearm while forming a right L-Stance toward
              C, pulling the left foot.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">22.</span>
            <span className="text-gray-700">
              Move the right foot to C forming a right Front Stance toward C while executing a middle thrust to C with the right straight finger tip.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">23.</span>
            <span className="text-gray-700">
              Move the left foot to B turning counter clockwise to form a left Front Stance toward B while executing a high block to B with the left
              double forearm.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">24.</span>
            <span className="text-gray-700">
              Move the right foot to B forming a Sitting Stance toward C while executing a middle front block to C with the right forearm and then a high
              side strike to B with the right back fist.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">25.</span>
            <span className="text-gray-700">
              Execute a middle side piercing kick to A with the right foot turning counter clockwise and then lower it to A.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">26.</span>
            <span className="text-gray-700">Execute a middle side piercing kick to A with the left foot turning clockwise.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">27.</span>
            <span className="text-gray-700">
              Lower the left foot to A and then execute a checking block to B with an X-knife-hand while forming a left L-Stance toward B pivoting with
              the left foot.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">28.</span>
            <span className="text-gray-700">
              Move the left foot to B forming a left Front Stance toward B while executing an upward block to B with a twin palm.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">29.</span>
            <span className="text-gray-700">
              Move the left foot on line AB and then execute a rising block with the right forearm while forming a right Front Stance toward A.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">30.</span>
            <span className="text-gray-700">Execute a middle punch to A with the left fist while maintaining a right Front Stance toward A.</span>
          </li>
          <li className="flex">
            <span className="mr-2 text-taekwondo-red">End:</span>
            <span className="text-gray-700">Bring the left foot back to a ready posture.</span>
          </li>
        </ol>
      </div>
    </div>
  );
}

export default function ChoongMoo({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: ChoongMooProps) {
  if (!["BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["choong_moo", "choongmoo"]) ? (
      <button
        type="button"
        className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["choong_moo", "choongmoo"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">Choong Moo</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-history" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="Choong Moo">
            <TabsTrigger value="pattern-history" className={triggerClass}>
              Pattern History
            </TabsTrigger>
            <TabsTrigger value="pattern-significance" className={triggerClass}>
              Pattern Significance
            </TabsTrigger>
            <TabsTrigger value="movements" className={triggerClass}>
              Movements
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-history" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern History</h4>
              <p className="mb-4 text-gray-700">
                ChoongMoo is an ITF pattern that Master Juliet performed for her 1st Dan Black Belt. It is named after Admiral Yi Sun-sin, also known as
                Admiral Yi, who was given the title &quot;ChoongMoo&quot; by the Korean king.
              </p>
              <p className="text-gray-700">
                Admiral Yi is renowned for inventing the Kubutson (turtle boat), which is considered the precursor to the modern-day submarine. This
                revolutionary naval vessel played a crucial role in Korean naval warfare history.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="pattern-significance" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern Significance</h4>
              <p className="text-gray-700">
                The pattern ends with a left-hand attack to symbolize Admiral Yi&apos;s tragic death, having no chance to show his loyalty to the king
                by using his strong right hand. The pattern&apos;s movements embody the loyalty, innovation, and strategic brilliance that characterized
                Admiral Yi&apos;s service to his country.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Movements</h4>
              <p className="text-gray-700">
                The pattern consists of 30 movements that demonstrate advanced techniques with an emphasis on power, precision, and strategic
                combinations. The movements incorporate both traditional techniques and innovative combinations, reflecting Admiral Yi&apos;s blend of
                conventional wisdom and tactical innovation.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Advanced stance transitions</li>
                <li>Complex hand techniques</li>
                <li>Strategic blocking combinations</li>
                <li>Power generation methods</li>
                <li>Precision striking patterns</li>
                <li>Dynamic balance control</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Technical precision</li>
                <li>Power development</li>
                <li>Strategic movement</li>
                <li>Balance and control</li>
                <li>Mental discipline</li>
                <li>Historical appreciation</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <div className="rounded-lg bg-white/60 p-4">
              <h4 className="mb-2 text-base font-medium text-taekwondo-red">Pattern</h4>
              <p className="mb-4 text-sm text-gray-600">Stance abbreviations (KEY) and full sequence (MOVESET).</p>
              <ChoongMooKeyAndMoveset />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {demoBtn && <div>{demoBtn}</div>}

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Pattern History</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="mb-4 text-gray-700">
            ChoongMoo is an ITF pattern that Master Juliet performed for her 1st Dan Black Belt. It is named after Admiral Yi Sun-sin, also known as
            Admiral Yi, who was given the title &quot;ChoongMoo&quot; by the Korean king.
          </p>
          <p className="text-gray-700">
            Admiral Yi is renowned for inventing the Kubutson (turtle boat), which is considered the precursor to the modern-day submarine. This
            revolutionary naval vessel played a crucial role in Korean naval warfare history.
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Pattern Significance</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="text-gray-700">
            The pattern ends with a left-hand attack to symbolize Admiral Yi&apos;s tragic death, having no chance to show his loyalty to the king by
            using his strong right hand. The pattern&apos;s movements embody the loyalty, innovation, and strategic brilliance that characterized Admiral
            Yi&apos;s service to his country.
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Movements</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <p className="text-gray-700">
            The pattern consists of 30 movements that demonstrate advanced techniques with an emphasis on power, precision, and strategic combinations.
            The movements incorporate both traditional techniques and innovative combinations, reflecting Admiral Yi&apos;s blend of conventional wisdom
            and tactical innovation.
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Key Techniques</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Advanced stance transitions</li>
            <li>Complex hand techniques</li>
            <li>Strategic blocking combinations</li>
            <li>Power generation methods</li>
            <li>Precision striking patterns</li>
            <li>Dynamic balance control</li>
          </ul>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Training Focus</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Technical precision</li>
            <li>Power development</li>
            <li>Strategic movement</li>
            <li>Balance and control</li>
            <li>Mental discipline</li>
            <li>Historical appreciation</li>
          </ul>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Moveset</h3>
        <div className="space-y-4 pt-1">
          <ChoongMooKeyAndMoveset />
        </div>
      </div>
    </div>
  );
}
